// Shared helpers for the Price List / Inventory / Quotations feature.
// Follows this codebase's existing convention: raw axios + a bearer token
// read from localStorage per call (see product_search.js), rather than a
// shared axios instance or a state library.
import axios from 'axios';
import { API_BASE_URL } from './config.js';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access')}`,
});

// The current draft Quotation (the "Quote Basket") is server-side, not
// localStorage-backed, so components that show its item count (e.g. the
// NavBar badge) need to know when it changes. There's no Context/Redux in
// this codebase, so a plain window event is the lightest way to notify
// everyone after a mutation - see QUOTATION_UPDATED_EVENT below.
export const QUOTATION_UPDATED_EVENT = 'quotation-updated';

const notifyQuotationUpdated = () => {
  window.dispatchEvent(new Event(QUOTATION_UPDATED_EVENT));
};

export const PRICE_CATEGORY_LABELS = {
  list_price: 'List Price',
  inline_standard: 'In-line Standard Value',
  inline_di_standard: 'In-line DI Standard Value',
  close_out_standard: 'Close Out Standard Value',
  promotional_standard: 'Promotional Standard Value',
};

export const getCurrentPriceList = async (sku) => {
  const response = await axios.get(`${API_BASE_URL}/api/price-list/`, {
    params: { sku },
    headers: authHeaders(),
  });
  return response.data;
};

// Full price history (including expired rows) for one sku - used to build
// the Price Category dropdowns, which now offer expired categories too
// (flagged in the UI) rather than hiding them like getCurrentPriceList does.
export const getAllPriceHistoryForSku = async (sku) => {
  const response = await axios.get(`${API_BASE_URL}/api/price-list/`, {
    params: { sku, all: 'true' },
    headers: authHeaders(),
  });
  return response.data;
};

export const getInventory = async (sku, months) => {
  const response = await axios.get(`${API_BASE_URL}/api/inventory/${encodeURIComponent(sku)}/`, {
    params: months ? { months } : undefined,
    headers: authHeaders(),
  });
  return response.data;
};

export const getCurrentQuotation = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/quotations/current/`, {
    headers: authHeaders(),
  });
  return response.data;
};

export const listQuotations = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/quotations/`, {
    headers: authHeaders(),
  });
  return response.data;
};

export const getQuotation = async (quotationId) => {
  const response = await axios.get(`${API_BASE_URL}/api/quotations/${quotationId}/`, {
    headers: authHeaders(),
  });
  return response.data;
};

export const addQuotationItem = async ({ sku, priceCategory, quantity = 1 }) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/quotations/current/items/`,
    { sku, price_category: priceCategory, quantity },
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

// Generic line-item PATCH - the backend accepts quantity, price_category,
// and selling_price independently, each only applied when present in the
// body (see QuotationCurrentItemDetailView.patch). Callers pass just the
// field(s) they changed.
export const updateQuotationItem = async (itemId, fields) => {
  const response = await axios.patch(
    `${API_BASE_URL}/api/quotations/current/items/${itemId}/`,
    fields,
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

export const updateQuotationItemQuantity = (itemId, quantity) => updateQuotationItem(itemId, { quantity });

export const removeQuotationItem = async (itemId) => {
  const response = await axios.delete(
    `${API_BASE_URL}/api/quotations/current/items/${itemId}/`,
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

// --- Quotation-id-scoped line item editing (draft OR saved) ---
// The functions above always target the user's current draft (used by
// NavBar/product_search/product_details when there's no specific quotation
// in view). quotation_detail.js knows its quotationId from the route and
// now supports fully editing a SAVED quotation too, not just a draft - so
// it uses these instead, which work on any quotation the user owns.

// Lightweight product search for the "Add Item" picker on a quotation -
// same endpoint/shape product_search.js uses, factored out here so it's not
// duplicated ad-hoc.
export const searchProducts = async (search) => {
  const response = await axios.get(`${API_BASE_URL}/api/products/`, {
    params: { search, page_size: 10 },
    headers: authHeaders(),
  });
  return response.data.results;
};

export const addQuotationLineItem = async (quotationId, { sku, priceCategory, quantity = 1 }) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/quotations/${quotationId}/items/`,
    { sku, price_category: priceCategory, quantity },
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

export const updateQuotationLineItem = async (quotationId, itemId, fields) => {
  const response = await axios.patch(
    `${API_BASE_URL}/api/quotations/${quotationId}/items/${itemId}/`,
    fields,
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

export const removeQuotationLineItem = async (quotationId, itemId) => {
  const response = await axios.delete(
    `${API_BASE_URL}/api/quotations/${quotationId}/items/${itemId}/`,
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

export const saveQuotation = async (quotationId, name) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/quotations/${quotationId}/save/`,
    name ? { name } : {},
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

// Re-snapshots one line's Our Price (unit_price) to its SKU/category's
// current price - the one deliberate exception to a saved quotation's
// read-only-ness. Returns the updated Quotation, same shape as every other
// quotation-mutating call here.
export const refreshQuotationLineItemPrice = async (quotationId, itemId) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/quotations/${quotationId}/items/${itemId}/refresh-price/`,
    {},
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

// Bulk version - refreshes every line whose current price differs from its
// frozen unit_price in one request. Returns { updated_count, quotation }.
export const refreshQuotationPrices = async (quotationId) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/quotations/${quotationId}/refresh-prices/`,
    {},
    { headers: authHeaders() },
  );
  notifyQuotationUpdated();
  return response.data;
};

export const deleteQuotation = async (quotationId) => {
  await axios.delete(`${API_BASE_URL}/api/quotations/${quotationId}/`, {
    headers: authHeaders(),
  });
  notifyQuotationUpdated();
};

export const getExportColumns = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/quotations/export-columns/`, {
    headers: authHeaders(),
  });
  return response.data;
};

// selectedColumnKeys: array of column keys to include, or null/undefined to
// let the backend default to "everything". An empty array is a real,
// distinct choice (Unselect All) - it must still be sent as `columns=`, not
// omitted, or the backend can't tell it apart from "no preference".
export const quotationExportUrl = (quotationId, format, selectedColumnKeys) => {
  const base = `${API_BASE_URL}/api/quotations/${quotationId}/export/${format}/`;
  if (!selectedColumnKeys) return base;
  return `${base}?columns=${encodeURIComponent(selectedColumnKeys.join(','))}`;
};

// PowerPoint's field list (one-product-per-slide) is a different, larger
// set than the Excel/PDF vendor cost-sheet columns - see
// API/quotation_exports.py::PPTX_FIELD_DEFINITIONS.
export const getPptxFields = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/quotations/export-pptx-fields/`, {
    headers: authHeaders(),
  });
  return response.data;
};

// PPTX export is POST (unlike Excel/PDF's GET) since it also carries a
// free-text design brief, which doesn't belong in a URL. Returns the raw
// blob response so the caller can drive the download UI (progress state,
// filename parsing) exactly like the GET-based exports do. `signal` (an
// AbortController's signal) lets the caller offer a genuine Cancel button
// while the AI design/slide-building is in progress. `plain` (the "use the
// plain white default template" toggle) skips the AI call AND strips every
// decorative shape - not just a blank design brief, which still gets
// DEFAULT_PPTX_THEME's gold accent bars/frame.
export const exportQuotationPptx = async (quotationId, selectedFieldKeys, designDescription, signal, plain = false) => {
  return axios.post(
    `${API_BASE_URL}/api/quotations/${quotationId}/export/pptx/`,
    { fields: selectedFieldKeys || null, design_description: designDescription || '', plain },
    { headers: authHeaders(), responseType: 'blob', signal },
  );
};
